export interface UserPlan {
  name: string;
  credits: number;
  subscriptionStatus?: string;
  trialEndsAt?: string | null;
}

export interface Subscription {
  plan: string;
  planName: string;
  limits: {
    textsPerMonth: number | 'unlimited';
    chatsPerMonth: number | 'unlimited';
    imagesPerMonth: number;
    metaAccounts: number | 'unlimited';
  };
  usage: {
    textsGenerated: number;
    chatsCreated: number;
    imagesGenerated: number;
  };
  usagePercentages?: {
    texts: number | null;
    chats: number | null;
    images: number | null;
  };
  billingCycle: 'MONTHLY' | 'YEARLY';
  currentCycleStart: string;
  currentCycleEnd: string;
  status: 'active' | 'canceled' | 'suspended' | 'past_due';
  isCustomPlan: boolean;
  customNote?: string;
}

export interface UserMeResponse {
  _id: string;
  email: string;
  name: string;
  phone?: string;
  cpf?: string;
  birthDate?: string;
  plan: UserPlan;
  credits: number;
  createdAt: string;
  isTrialExpired?: boolean;
  subscriptionStatus?: string;
  currentCycleEnd?: string;
  subscription?: Subscription;
}
export interface AuthResponse { token: string; }
export interface GenerationItem { _id: string; createdAt: string; promptData?: { prompt: string }; tokens?: number; }
export interface GenerationList { total: number; items: GenerationItem[]; }

// URLs disponíveis
// const API_BASE = 'http://localhost:3000';
const API_BASEE = 'https://backend.copygenius.gustavohenrique.dev';

// Usando DEV por padrão em desenvolvimento local
const API_BASE = import.meta.env.VITE_API_BASE_URL || API_BASEE;
// Descomente a linha abaixo para usar PROD:
// const API_BASE = import.meta.env.VITE_API_BASE_URL || API_BASE_PROD;

// Export para uso em outros componentes
export const API_BASE_URL = API_BASE;

async function request<T>(path: string, options: RequestInit = {}, token?: string): Promise<T> {
  const headers: Record<string,string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  const res = await fetch(API_BASE + path, { ...options, headers: { ...headers, ...(options.headers||{}) } });
  if (res.status === 204) return undefined as unknown as T;
  let data: any = null;
  try { data = await res.json(); } catch {/* ignore */}
  if (!res.ok) {
    const msg = data?.message || data?.error || res.status + ' HTTP Error';
    throw new Error(msg);
  }
  return data as T;
}

export const api = {
  register: (data: {email:string; password:string; name:string; birthDate:string; phone?:string; cpf?:string}) => request<AuthResponse>('/api/auth/register', { method:'POST', body: JSON.stringify(data) }),
  login: (email:string, password:string) => request<AuthResponse>('/api/auth/login', { method:'POST', body: JSON.stringify({email,password}) }),
  forgot: (email:string) => request<{message:string; token?:string}>('/api/auth/forgot', { method:'POST', body: JSON.stringify({email}) }),
  reset: (token:string, newPassword:string) => request<{message:string}>('/api/auth/reset', { method:'POST', body: JSON.stringify({token,newPassword}) }),
  me: (token:string) => request<UserMeResponse>('/api/me', { method:'GET' }, token),
  updateProfile: (data: {name?:string; phone?:string; cpf?:string; birthDate?:string}, token:string) => request<{message:string; user:any}>('/api/me', { method:'PUT', body: JSON.stringify(data) }, token),
  generate: (prompt:string, token:string) => request<any>('/api/generate', { method:'POST', body: JSON.stringify({prompt}) }, token),
  generations: (params: {from?:string; to?:string}, token:string) => {
    const qs = new URLSearchParams();
    if(params.from) qs.append('from', params.from);
    if(params.to) qs.append('to', params.to);
    return request<GenerationList>('/api/generations'+(qs.toString()?('?'+qs.toString()):''), { method:'GET' }, token);
  },
  deleteGeneration: (id:string, token:string) => request<void>('/api/generations/'+id, { method:'DELETE' }, token),
  upgrade: (newPlan:string, token:string) => request<any>('/api/subscriptions/upgrade', { method:'POST', body: JSON.stringify({newPlan}) }, token),
  createSubscription: (
    plan:string,
    cycle:'MONTHLY'|'YEARLY',
    billingType:'BOLETO'|'CREDIT_CARD'|'PIX',
    token:string,
    creditCard?: {
      holderName: string;
      number: string;
      expiryMonth: string;
      expiryYear: string;
      ccv: string;
    },
    creditCardHolderInfo?: {
      name: string;
      email: string;
      cpfCnpj: string;
      postalCode: string;
      addressNumber: string;
      addressComplement?: string;
      phone: string;
    },
    cpfCnpj?: string // CPF para PIX/Boleto
  ) =>
    request<{
      message:string;
      subscriptionId:string;
      plan:string;
      cycle:string;
      value:number;
      billingType:string;
      payment?: {
        id: string;
        invoiceUrl?: string;
        bankSlipUrl?: string;
        status: string;
        value: number;
        dueDate: string;
      };
      pix?: {
        qrCode: string;
        encodedImage: string;
        expirationDate: string;
      };
      bankSlipUrl?: string;
    }>('/api/subscriptions/create', {
      method:'POST',
      body: JSON.stringify({plan, cycle, billingType, creditCard, creditCardHolderInfo, cpfCnpj})
    }, token),
  cancelSubscription: (token:string) => request<any>('/api/subscriptions/cancel', { method:'POST' }, token),

  // Chat/Conversas
  createConversation: (token:string) => request<{conversationId:string; title:string; messages:any[]; createdAt:string}>('/api/chat/conversations', { method:'POST' }, token),
  listConversations: (token:string) => request<Array<{_id:string; title:string; lastMessage:string; messageCount:number; updatedAt:string}>>('/api/chat/conversations', { method:'GET' }, token),
  getConversation: (id:string, token:string) => request<{_id:string; title:string; messages:any[]; createdAt:string; updatedAt:string}>(`/api/chat/conversations/${id}`, { method:'GET' }, token),
  sendMessage: (
    conversationId: string,
    message: string,
    token: string,
    attachments?: Array<{fileName: string; mimeType: string; base64Data: string}>
  ) => request<{
    message: {role: string; content: string; timestamp: string; attachments?: any[]};
    creditsRemaining: number;
    conversationTitle: string;
    processedFiles?: Array<{fileId: string; fileName: string; mimeType: string}>;
  }>(`/api/chat/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: JSON.stringify({message, attachments})
  }, token),
  deleteConversation: (id:string, token:string) => request<{message:string}>(`/api/chat/conversations/${id}`, { method:'DELETE' }, token),
  updateConversationTitle: (id:string, title:string, token:string) => request<{message:string; title:string}>(`/api/chat/conversations/${id}/title`, { method:'PUT', body: JSON.stringify({title}) }, token),
  clearConversationMessages: (id:string, token:string) => request<{message:string; conversation:{_id:string; title:string; messages:any[]}}>(`/api/chat/conversations/${id}/messages`, { method:'DELETE' }, token),

  // Social Posts
  generateSocialPost: (
    data: {
      platform: string;
      objective: string;
      audience?: string;
      brandVoice?: string;
      keyOffer?: string;
      generateImage: boolean;
      visualStyle?: string;
      imageSize?: string;
      imageCount?: number;
    },
    token: string
  ) => request<{post: any; creditsRemaining: number}>('/api/social-posts/generate', { method:'POST', body: JSON.stringify(data) }, token),

  regenerateSocialPostImage: (
    postId: string,
    visualStyle: string,
    token: string
  ) => request<{imageUrls: string[]; creditsRemaining: number}>(`/api/social-posts/${postId}/generate-image`, { method:'POST', body: JSON.stringify({visualStyle}) }, token),

  editSocialImage: (
    data: {
      imageUrl: string;
      editPrompt: string;
      imageSize?: string;
    },
    token: string
  ) => request<{editedImageUrl: string; creditsRemaining: number}>('/api/social-posts/edit-image', { method:'POST', body: JSON.stringify(data) }, token),

  createPostFromImage: (
    data: {
      imageBase64: string;
      platform: string;
      additionalContext?: string;
      brandVoice?: string;
      keyOffer?: string;
      imageSize?: string;
    },
    token: string
  ) => request<{post: any; subscription: any}>('/api/social-posts/create-from-image', { method:'POST', body: JSON.stringify(data) }, token),

  // Integração Social (Meta - Instagram/Facebook)
  enableAdvancedMode: (termsAccepted: boolean, privacyAccepted: boolean, token: string) =>
    request<{message: string; advancedModeEnabled: boolean}>('/api/social/advanced-mode/enable', { method:'POST', body: JSON.stringify({termsAccepted, privacyAccepted}) }, token),

  disableAdvancedMode: (token: string) =>
    request<{message: string; advancedModeEnabled: boolean}>('/api/social/advanced-mode/disable', { method:'POST' }, token),

  initiateOAuth: (token: string) =>
    request<{authUrl: string}>('/api/social/auth/facebook/initiate', { method:'GET' }, token),

  listConnectedAccounts: (token: string) =>
    request<{accounts: Array<{_id: string; platform: string; accountId: string; accountName: string; accountUsername?: string; profilePictureUrl?: string; isActive: boolean; createdAt: string}>}>('/api/social/accounts', { method:'GET' }, token),

  disconnectAccount: (accountId: string, token: string) =>
    request<{message: string}>(`/api/social/accounts/${accountId}`, { method:'DELETE' }, token),

  publishToSocial: (accountId: string, imageUrls: string[], caption: string, token: string) =>
    request<{message: string; result: any; accountName: string}>('/api/social/publish', { method:'POST', body: JSON.stringify({accountId, imageUrls, caption}) }, token),

  // LGPD - Direitos do Titular
  exportUserData: (token: string) =>
    request<any>('/api/me/export', { method: 'GET' }, token),

  deleteAccount: (confirmation: string, token: string) =>
    request<{
      message: string;
      lgpdCompliance: {
        law: string;
        article: string;
        deletedData: {
          generations: number;
          billingEvents: number;
          conversations: number;
          socialAccounts: number;
          userProfile: number;
        };
        deletedAt: string;
      };
    }>('/api/me', { method: 'DELETE', body: JSON.stringify({ confirmation }) }, token)
};

// Admin API (Basic Auth)
async function adminRequest<T>(path: string, authHeader: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string,string> = {
    'Content-Type': 'application/json',
    'Authorization': authHeader
  };
  const res = await fetch(API_BASE + path, { ...options, headers: { ...headers, ...(options.headers||{}) } });
  if (res.status === 204) return undefined as unknown as T;
  let data: any = null;
  try { data = await res.json(); } catch {/* ignore */}
  if (!res.ok) {
    const msg = data?.message || data?.error || res.status + ' HTTP Error';
    throw new Error(msg);
  }
  return data as T;
}

export const adminApi = {
  getStats: (authHeader: string) => adminRequest<{totalUsers:number; activeSubscriptions:number; trialUsers:number; proUsers:number}>('/api/admin/stats', authHeader),
  listUsers: (authHeader: string) => adminRequest<{total:number; users:any[]}>('/api/admin/users', authHeader),
  getUserById: (id:string, authHeader: string) => adminRequest<any>(`/api/admin/users/${id}`, authHeader),
  updateCredits: (id:string, credits:number, authHeader: string) => adminRequest<any>(`/api/admin/users/${id}/credits`, authHeader, { method:'PUT', body: JSON.stringify({credits}) }),
  updatePlan: (id:string, plan:string, authHeader: string) => adminRequest<any>(`/api/admin/users/${id}/plan`, authHeader, { method:'PUT', body: JSON.stringify({plan}) }),
  updateSubscriptionStatus: (id:string, status:string, authHeader: string) => adminRequest<any>(`/api/admin/users/${id}/subscription-status`, authHeader, { method:'PUT', body: JSON.stringify({status}) }),
  deleteUser: (id:string, authHeader: string) => adminRequest<{message:string; deletedUser:{email:string; name:string}}>(`/api/admin/users/${id}`, authHeader, { method:'DELETE' }),

  // Novo sistema de subscription
  addImageGenerations: (id:string, amount:number, type:'limit'|'onetime', authHeader: string) =>
    adminRequest<{message:string; user:any}>(`/api/admin/users/${id}/add-images`, authHeader, { method:'POST', body: JSON.stringify({amount, type}) }),
  resetUserUsage: (id:string, authHeader: string) =>
    adminRequest<{message:string; user:any}>(`/api/admin/users/${id}/reset-usage`, authHeader, { method:'POST' })
};
